import { useCallback, useEffect, useRef, useState } from 'react';
import registryService from '../services/registryService';

const LOAD_ERROR = 'Non riesco a caricare lo storico delle sincronizzazioni: riprova tra poco.';

// Anche un'eccezione sincrona del servizio diventa una promise rifiutata (e passa dal finally).
const fetchRuns = (registryId, params) => {
  try {
    return Promise.resolve(registryService.getSyncRuns(registryId, params));
  } catch (e) {
    return Promise.reject(e);
  }
};

/**
 * Storico delle sincronizzazioni di un'anagrafica (paginato lato server) e ultima esecuzione.
 *
 * L'ultima esecuzione e' la prima riga dell'ultima risposta di pagina 0 (lo storico e'
 * ordinato dal piu' recente). Finche' e' RUNNING il hook ricarica la pagina corrente ogni
 * `pollMs` senza toccare `loading`; se l'utente sta guardando un'altra pagina, rilegge
 * anche la pagina 0 solo per aggiornare l'ultima esecuzione, cosi' il polling si ferma.
 * Un tick con una richiesta ancora in volo viene saltato: con risposte piu' lente di
 * `pollMs` ogni tick scarterebbe la risposta precedente e lo stato non si aggiornerebbe mai.
 * Le richieste in volo sono contate (non un flag): ognuna si scala da sola quando si chiude,
 * anche se nel frattempo una richiesta piu' recente l'ha resa obsoleta.
 */
const useRegistrySyncRuns = (registry, { pollMs = 3000, size = 20 } = {}) => {
  const registryId = registry?.id ?? null;
  const [page, setPage] = useState(0);
  const [runs, setRuns] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [lastRun, setLastRun] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const requestId = useRef(0);
  const lastRunRequestId = useRef(0);
  const pending = useRef(0);
  const lastRunPending = useRef(0);

  // Cambio anagrafica: reset durante il render, la prima richiesta parte gia' da pagina 0.
  const [scopeId, setScopeId] = useState(registryId);
  if (scopeId !== registryId) {
    setScopeId(registryId);
    setPage(0);
    setRuns([]);
    setTotalElements(0);
    setLastRun(null);
    setError(null);
  }

  const fetchPage = useCallback((targetPage, { quiet = false } = {}) => {
    const id = ++requestId.current;
    if (targetPage === 0) lastRunRequestId.current += 1;
    pending.current += 1;
    if (!quiet) setLoading(true);
    fetchRuns(registryId, { page: targetPage, size })
      .then((data) => {
        if (id !== requestId.current) return;
        const content = Array.isArray(data?.content) ? data.content : [];
        setRuns(content);
        setTotalElements(Number(data?.totalElements) || 0);
        if (targetPage === 0) setLastRun(content[0] ?? null);
        setError(null);
      })
      .catch(() => {
        if (id !== requestId.current) return;
        setError(LOAD_ERROR);
        if (!quiet) {
          setRuns([]);
          setTotalElements(0);
        }
      })
      .finally(() => {
        pending.current -= 1;
        if (id === requestId.current) setLoading(false);
      });
  }, [registryId, size]);

  const refreshLastRun = useCallback(() => {
    const id = ++lastRunRequestId.current;
    lastRunPending.current += 1;
    fetchRuns(registryId, { page: 0, size })
      .then((data) => {
        if (id !== lastRunRequestId.current) return;
        setLastRun(Array.isArray(data?.content) ? data.content[0] ?? null : null);
      })
      .catch(() => {})
      .finally(() => {
        lastRunPending.current -= 1;
      });
  }, [registryId, size]);

  useEffect(() => {
    if (!registryId) {
      // invalida le richieste in volo dell'anagrafica precedente
      requestId.current += 1;
      lastRunRequestId.current += 1;
      setLoading(false);
      return;
    }
    fetchPage(page);
  }, [registryId, page, reloadToken, fetchPage]);

  const running = lastRun?.status === 'RUNNING';

  useEffect(() => {
    if (!running || !registryId) return undefined;
    const handle = setInterval(() => {
      if (pending.current === 0) fetchPage(page, { quiet: true });
      if (page !== 0 && lastRunPending.current === 0) refreshLastRun();
    }, pollMs);
    return () => clearInterval(handle);
  }, [running, registryId, page, pollMs, fetchPage, refreshLastRun]);

  // Al ritorno a pagina 0 il run piu' recente e' in testa, e il polling lo vede.
  const reload = useCallback(() => {
    setPage(0);
    setReloadToken((t) => t + 1);
  }, []);

  return { runs, totalElements, page, setPage, loading, error, reload, lastRun, running };
};

export default useRegistrySyncRuns;
